import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "../config";
import PageBanner from "./banner";
import PatientLogoutButton from "./PatientLogoutButton";
import { FaArrowLeft } from "react-icons/fa";


/* =====================
   ICONS
===================== */
const greenIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

const redIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

const blackIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-black.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

/* =====================
   COMPONENT
===================== */
const PatientMap = () => {
  const navigate = useNavigate();
  const patientID = localStorage.getItem("patientID");
  const token = localStorage.getItem("token");

  /* =====================
     FILTER STATE
  ===================== */
  const [language, setLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");

  const [filters, setFilters] = useState({});
  const [patient, setPatient] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loadingWorkerID, setLoadingWorkerID] = useState(null);

  /* =====================
     STARS
  ===================== */
  const renderStars = (rawRating) => {
    const rating = Number(rawRating) || 0;
    const maxStars = 4;

    return (
      <div style={{ color: "#FFD700", fontSize: "16px" }}>
        {"★".repeat(rating)}
        {"☆".repeat(maxStars - rating)}
      </div>
    );
  };

  /* =====================
     FETCH MAP DATA
  ===================== */
  const fetchMapData = useCallback(() => {
    axios
      .get(`${config.API_BASE_URL}/patient/map`, {
        params: { patientID, ...filters },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        setPatient(res.data.patient);
        setWorkers(res.data.workers);
      })
      .catch(() => {});
  }, [patientID, filters, token]);

  useEffect(() => {
    fetchMapData();
    const interval = setInterval(fetchMapData, 5000);
    return () => clearInterval(interval);
  }, [fetchMapData]);

  /* =====================
     TOGGLE ACTIVATION
  ===================== */
  const toggleActivation = async (workerID, isActive) => {
    try {
      setLoadingWorkerID(workerID);

      const endpoint = isActive
        ? "/patient/deactivate-worker"
        : "/patient/activate-worker";

      await axios.post(
        `${config.API_BASE_URL}${endpoint}`,
        { patientID, workerID },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchMapData();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingWorkerID(null);
    }
  };

  /* =====================
     FILTER ACTIONS
  ===================== */
  const applyFilter = () => {
    setFilters({
      language: language || undefined,
      gender: gender || undefined,
      country: country || undefined,
      city: city || undefined
    });
  };

  const clearFilter = () => {
    setLanguage("");
    setGender("");
    setCountry("");
    setCity("");
    setFilters({});
  };

  return (
    <>
      <PageBanner
        title="UberCare"
        subtitle=""
        rightContent={<PatientLogoutButton />}
      />

      <div style={layout}>
        {/* =====================
            FILTER PANEL
        ===================== */}
        <div style={filterPanel}>
          <button
            style={backBtn}
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft />
            Back
          </button>

          <h3 style={filterTitle}>Filter Remote Workers</h3>

          <div style={field}>
            <label style={label}>Language</label>
            <input
              style={input}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            />
          </div>

          <div style={field}>
            <label style={label}>Gender</label>
            <select
              style={input}
              value={gender}
              onChange={(e) => setGender(e.target.value)}
            >
              <option value="">All</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </div>

          <div style={field}>
            <label style={label}>Country</label>
            <input
              style={input}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </div>

          <div style={field}>
            <label style={label}>City</label>
            <input
              style={input}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>

          <div style={buttonRow}>
            <button style={applyBtn} onClick={applyFilter}>
              Apply
            </button>
            <button style={clearBtn} onClick={clearFilter}>
              Clear
            </button>
          </div>
        </div>

        {/* =====================
            MAP
        ===================== */}
        <MapContainer
          center={[20, 0]}
          zoom={2}
          minZoom={2}
          style={{ height: "100vh", width: "100%" }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {/* WORKERS */}
          {workers.map((w) => (
            <Marker
              key={w.workerID}
              position={[w.latitude, w.longitude]}
              icon={w.status === "available" ? greenIcon : redIcon}
            >
              <Popup maxWidth={300}>
                <strong>
                  {w.firstName} {w.lastName}
                </strong>
                <hr />

                <p><b>Gender:</b> {w.gender === "M" ? "Male" : "Female"}</p>
                <p><b>Language:</b> {w.spokenLanguage}</p>
                <p><b>Location:</b> {w.city}, {w.country}</p>
                <p><b>Phone:</b> {w.whatsapp}</p>

                {w.status === "available" ? (
                  <a
                    href={`https://wa.me/${w.whatsapp.replace("+", "")}`}
                    target="_blank"
                    rel="noreferrer"
                    style={whatsAppBtn}
                  >
                    Call on WhatsApp
                  </a>
                ) : (
                  <button disabled style={disabledBtn}>
                    Worker Unavailable
                  </button>
                )}

                <p style={{ marginTop: "10px" }}><b>Rating:</b></p>
                {renderStars(w.rating)}

                <p><b>Rate:</b> ${w.rate}/hr</p>

                <p>
                  <b>Status:</b>{" "}
                  <span style={{ color: w.status === "available" ? "green" : "red" }}>
                    {w.status}
                  </span>
                </p>

                {/* ACTIVATE / DEACTIVATE */}
                <button
                  style={{
                    ...actionBtn,
                    background: w.activated ? "#dc3545" : "#198754",
                    opacity: loadingWorkerID === w.workerID ? 0.7 : 1
                  }}
                  disabled={loadingWorkerID === w.workerID}
                  onClick={() => toggleActivation(w.workerID, w.activated)}
                >
                  {w.activated ? "Deactivate Worker" : "Activate Worker"}
                </button>
              </Popup>
            </Marker>
          ))}

          {/* PATIENT */}
          {patient && (
            <Marker
              position={[patient.latitude, patient.longitude]}
              icon={blackIcon}
            >
              <Popup>
                <strong>You</strong>
                <hr />
                <p><b>Name:</b> {patient.firstName} {patient.lastName}</p>
                <p><b>Language:</b> {patient.spokenLanguage}</p>
                <p><b>Location:</b> {patient.city}, {patient.country}</p>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </>
  );
};

export default PatientMap;

/* =====================
   STYLES
===================== */
const layout = { display: "flex", height: "100vh" };

const filterPanel = {
  width: "300px",
  padding: "20px",
  background: "#f8f9fa",
  borderRight: "1px solid #ddd"
};

const backBtn = {
  width: "100%",
  marginBottom: "12px",
  padding: "10px 12px",
  background: "transparent",
  color: "#0E5580",
  border: "1px solid #0E5580",
  borderRadius: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  fontWeight: "600"
};

const filterTitle = { marginBottom: "15px", fontSize: "18px", fontWeight: "600" };

const field = {
  marginBottom: "12px",
  display: "flex",
  flexDirection: "column"
};

const label = {
  fontSize: "13px",
  fontWeight: "600",
  marginBottom: "4px"
};

const input = {
  padding: "8px",
  fontSize: "14px",
  border: "1px solid #ccc",
  borderRadius: "4px"
};

const buttonRow = {
  display: "flex",
  gap: "10px",
  marginTop: "15px"
};

const applyBtn = {
  flex: 1,
  padding: "10px",
  background: "#0E5580",
  color: "#fff",
  border: "none",
  cursor: "pointer"
};

const clearBtn = {
  flex: 1,
  padding: "10px",
  background: "#6c757d",
  color: "#fff",
  border: "none",
  cursor: "pointer"
};


const whatsAppBtn = {
  display: "block",
  marginTop: "10px",
  padding: "8px",
  background: "#25D366",
  color: "#fff",
  textAlign: "center",
  textDecoration: "none",
  borderRadius: "4px"
};

const actionBtn = {
  marginTop: "10px",
  padding: "10px",
  width: "100%",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  borderRadius: "4px"
};

const disabledBtn = {
  marginTop: "10px",
  padding: "8px",
  width: "100%",
  background: "#ccc",
  border: "none"
};
